import React, { useMemo } from "react";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { Box, Paper, Typography } from "@mui/material";
import HPTooltip from "@/components/common/Tooltip";
import type { TimePeriod } from "@/store/slices/crosstab-config-slice";
import {
  getTimePeriodFooterText,
  getTimePeriodNominalRepresentation,
} from "@/utils/time-period-display";
import {
  getRollingAverageFooterText,
  getRollingAverageComparisonFooterText,
  getMissingDataWarning,
  getPeriodTypeLabel,
} from "@/utils/rolling-average-utils";
import {
  resolveTimePeriodInWindow,
  getTimePeriodDates,
} from "@/utils/time-period-utils";
import { NoDataIcon } from "@/assets/images";
import EmptyChartState from "@components/common/EmptyChartState";

const NAMED_PERIOD_VALUES = new Set([
  "latest_quarter",
  "current_quarter",
  "previous_quarter",
  "last_quarter",
  "latest_semester",
  "current_semester",
  "previous_semester",
  "last_semester",
]);

interface CrosstabTableProps {
  title?: string;
  rowLabelColumn?: string;
  data: Array<Record<string, string | number>>;
  height?: number;
  decimals?: number; // Number of decimal places to show
  hiddenRows?: string[]; // IDs of rows to hide
  hiddenColumns?: string[]; // IDs of columns to hide
  customRowLabels?: Record<string, string>; // Custom labels for rows
  customColumnLabels?: Record<string, string>; // Custom labels for columns
  rowOrder?: string[]; // Row IDs in display order (for drag-and-drop reordering)
  columnOrder?: string[]; // Column IDs in display order (for drag-and-drop reordering)
  showN?: boolean; // Show N values at the bottom of each column
  columnNValues?: Record<string, number | string>; // N values for each column (key is column field name), can be "-" for suppressed
  hasSuppressedCells?: boolean; // Whether any cells are suppressed due to low base size
  showPercentSign?: boolean; // Show % sign next to values (for Single/Multiple question types)
  statisticalTesting?: {
    enabled: boolean;
    testType?: string;
    significanceLevel?: string;
    minimumSampleSize?: number;
    compareToTotal?: boolean;
    referenceColumnEntity?: string | null;
  };
  timePeriodSelection?: {
    timePeriod: TimePeriod;
    startDate: string | null;
    endDate: string | null;
  };
  hasCustomTitle?: boolean; // Whether the user has customized the title
  weightOption?: "default" | "none" | "custom" | "per-segment";
  customWeight?: {
    questionId: string;
    variableId: string;
    questionLabel: string;
    variableLabel: string;
  } | null;
  rowCount?: number;
  hasActiveFilters?: boolean;
  dashboardDateOverride?: { startDate: string | null; endDate: string | null };
  dataAvailabilityDate?: string | null;
  rollingAverage?: {
    enabled: boolean;
    windowSize: number;
  };
  comparisonRollingLabel?: string;
  rollingPeriodsWarningMessage?: string;
  responsesMinDate?: string;
  effectiveRollingPeriodCount?: number;
  missingRollingPeriodLabels?: string[];
  viewerState?: {
    rollingAverage: number | null;
    timeUnit: string | null;
    timePeriod: string | null;
  };
}

/**
 * CrosstabTable Component
 *
 * A crosstab table with a row label column and multiple value columns.
 * Designed to match the style shown in the screenshot with MEN/WOMEN columns.
 *
 * @example
 * ```tsx
 * // Backend returns data like:
 * const backendData = [
 *   { id: 1, label: 'Overall RQ', men: 81, women: 79 },
 *   { id: 2, label: 'Total N', men: 510, women: 476 },
 * ];
 *
 * // Component automatically detects columns from data
 * <CrosstabTable
 *   data={backendData}
 *   rowLabelColumn="label"
 * />
 * ```
 */
const CrosstabTable: React.FC<CrosstabTableProps> = ({
  title,
  rowLabelColumn = "label",
  data,
  decimals = 0,
  hiddenRows = [],
  hiddenColumns = [],
  customRowLabels = {},
  customColumnLabels = {},
  rowOrder = [],
  columnOrder = [],
  showN = false,
  columnNValues = {},
  hasSuppressedCells = false,
  showPercentSign = false,
  statisticalTesting = { enabled: false },
  timePeriodSelection,
  hasCustomTitle = false,
  weightOption = "default",
  customWeight = null,
  rowCount,
  hasActiveFilters = false,
  dashboardDateOverride,
  dataAvailabilityDate,
  rollingAverage,
  comparisonRollingLabel,
  rollingPeriodsWarningMessage,
  effectiveRollingPeriodCount,
  missingRollingPeriodLabels,
  viewerState,
}) => {
  const statisticalTestingEnabled = statisticalTesting.enabled;
  const compareToTotalEnabled = statisticalTesting.compareToTotal || false;
  const referenceColumnEntity =
    statisticalTesting.referenceColumnEntity !== undefined
      ? statisticalTesting.referenceColumnEntity
      : null;
  const isOverallTotalMode =
    compareToTotalEnabled && referenceColumnEntity === null;
  const isReferenceColumnMode =
    compareToTotalEnabled && referenceColumnEntity !== null;

  // Check if any visible cells are suppressed (filters out hidden rows/columns)
  const hasVisibleSuppressedCells = React.useMemo(() => {
    if (!hasSuppressedCells) return false;

    return data
      .filter((row) => {
        const rowId = String(row.id || row[rowLabelColumn]);
        return !hiddenRows.includes(rowId);
      })
      .some((row) => {
        return Object.entries(row)
          .filter(
            ([key]) =>
              key !== "id" &&
              key !== rowLabelColumn &&
              key !== "_metrics" &&
              key !== "enableStatTesting" &&
              key !== "enableRowStatTesting" &&
              !hiddenColumns.includes(key),
          )
          .some(([, value]) => value === "-");
      });
  }, [data, hasSuppressedCells, hiddenRows, hiddenColumns, rowLabelColumn]);
  // Calculate time period footer text if needed
  const timePeriodFooterText = React.useMemo(() => {
    const viewerTP = viewerState?.timePeriod ?? null;
    const savedTP = timePeriodSelection?.timePeriod;
    const effectivePeriod = viewerTP ?? savedTP;

    if (!effectivePeriod || effectivePeriod === "all_time") return null;
    if (!hasCustomTitle) return null;

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getTimePeriodFooterText(
        viewerTP as TimePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getTimePeriodFooterText(
      effectivePeriod as TimePeriod,
      timePeriodSelection?.startDate,
      timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.timePeriod,
    timePeriodSelection,
    hasCustomTitle,
    dataAvailabilityDate,
  ]);

  // Calculate dashboard filter note for Module 4 contextual annotations
  const dashboardFilterNote = useMemo(() => {
    if (!dashboardDateOverride?.startDate || !dashboardDateOverride?.endDate)
      return null;
    // Viewer's selected period takes precedence over the saved config period.
    const rawTimePeriod =
      viewerState?.timePeriod ?? timePeriodSelection?.timePeriod;
    if (!rawTimePeriod || !NAMED_PERIOD_VALUES.has(rawTimePeriod)) {
      return `Time Period (filtered): ${dashboardDateOverride.startDate} – ${dashboardDateOverride.endDate}`;
    }
    // NAMED_PERIOD_VALUES.has() validates rawTimePeriod is a TimePeriod
    const effectiveTimePeriod = rawTimePeriod as TimePeriod;
    const effectiveEnd =
      dataAvailabilityDate &&
      dataAvailabilityDate < dashboardDateOverride.endDate
        ? dataAvailabilityDate
        : dashboardDateOverride.endDate;
    const resolved = resolveTimePeriodInWindow(
      effectiveTimePeriod,
      dashboardDateOverride.startDate,
      effectiveEnd,
    );
    if (!resolved) {
      const unit = effectiveTimePeriod.includes("semester")
        ? "semester"
        : "quarter";
      return `No complete ${unit} available within the selected date range.`;
    }
    const label = getTimePeriodNominalRepresentation(
      effectiveTimePeriod,
      resolved.startDate,
      resolved.endDate,
    );

    // If rolling average is enabled, show rolling average footer format.
    // Respect viewer's RA override: explicit 0 disables; positive replaces saved.
    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerExplicitlyDisabledRA = viewerRA === 0;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const savedRAEnabled =
      !!rollingAverage?.enabled && !!rollingAverage?.windowSize;
    const effectiveRAEnabled =
      !viewerExplicitlyDisabledRA && (hasViewerRA || savedRAEnabled);

    if (effectiveRAEnabled) {
      // Per PRD-35: the dashboard date filter does not restrict the rolling
      // average window size. Lookback may extend outside the filter range.
      // Use configured windowSize (or viewer's RA), NOT effectiveRollingPeriodCount.
      const windowSize = hasViewerRA
        ? (viewerRA as number)
        : (rollingAverage!.windowSize as number);
      const rollingText = getRollingAverageFooterText(
        windowSize,
        effectiveTimePeriod,
        resolved.startDate,
        resolved.endDate,
      );
      // Replace "Rolling Average:" with "Rolling Average (filtered):"
      return rollingText.replace(
        "Rolling Average:",
        "Rolling Average (filtered):",
      );
    }

    // If stat testing is enabled, show "X (filtered): A vs B" for all named period
    // types. The comparison period is always the complete period immediately before
    // the displayed period — per PRD-35 it is allowed to be outside the filter range.
    if (statisticalTestingEnabled) {
      const periodLabels: Record<string, string> = {
        latest_quarter: "Latest Quarter",
        previous_quarter: "Previous Quarter",
        latest_semester: "Latest Semester",
        previous_semester: "Previous Semester",
      };
      const periodLabel = periodLabels[effectiveTimePeriod];

      if (periodLabel) {
        const dayBefore = new Date(resolved.startDate);
        dayBefore.setDate(dayBefore.getDate() - 1);
        const dayBeforeStr = dayBefore.toISOString().split("T")[0];

        const comparisonType = effectiveTimePeriod.includes("semester")
          ? "latest_semester"
          : "latest_quarter";
        const comparisonResolved = resolveTimePeriodInWindow(
          comparisonType,
          "2000-01-01",
          dayBeforeStr,
        );

        if (comparisonResolved) {
          const comparisonLabel = getTimePeriodNominalRepresentation(
            comparisonType,
            comparisonResolved.startDate,
            comparisonResolved.endDate,
          );
          if (comparisonLabel) {
            return `${periodLabel} (filtered): ${label} vs ${comparisonLabel}`;
          }
        }
      }
    }

    return label ? `Time Period (filtered): ${label}` : null;
  }, [
    dashboardDateOverride,
    timePeriodSelection?.timePeriod,
    rollingAverage,
    effectiveRollingPeriodCount,
    statisticalTestingEnabled,
    viewerState?.timePeriod,
    viewerState?.rollingAverage,
    dataAvailabilityDate,
  ]);

  // Suppress stat testing when the dashboard date filter yields no complete period
  const isStatTestingSuppressed =
    !!dashboardFilterNote?.startsWith("No complete");

  // Calculate rolling average footer text if needed (only when title is customized)
  const rollingAverageFooterText = React.useMemo(() => {
    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerTP = viewerState?.timePeriod ?? null;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const viewerExplicitlyDisabledRA = viewerRA === 0;
    const savedRAEnabled = rollingAverage?.enabled && rollingAverage.windowSize;

    if (viewerExplicitlyDisabledRA) return null;

    if (!hasViewerRA && (!savedRAEnabled || !hasCustomTitle)) {
      return null;
    }

    const savedTP = timePeriodSelection?.timePeriod;
    const timePeriod = (viewerTP ?? savedTP) as TimePeriod | undefined;
    if (!timePeriod) return null;

    const effectiveWindowSize = hasViewerRA
      ? viewerRA
      : (effectiveRollingPeriodCount ?? rollingAverage!.windowSize);

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getRollingAverageFooterText(
        effectiveWindowSize,
        timePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getRollingAverageFooterText(
      effectiveWindowSize,
      timePeriod,
      timePeriodSelection?.startDate,
      timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.rollingAverage,
    viewerState?.timePeriod,
    rollingAverage,
    effectiveRollingPeriodCount,
    timePeriodSelection?.timePeriod,
    timePeriodSelection?.startDate,
    timePeriodSelection?.endDate,
    hasCustomTitle,
    dataAvailabilityDate,
  ]);

  // Calculate rolling average comparison footer text if needed
  const rollingAverageComparisonFooterText = React.useMemo(() => {
    const viewerRA = viewerState?.rollingAverage ?? null;
    const viewerTP = viewerState?.timePeriod ?? null;
    const hasViewerRA = viewerRA !== null && viewerRA !== 0;
    const viewerExplicitlyDisabledRA = viewerRA === 0;

    if (viewerExplicitlyDisabledRA) return null;

    const savedRAEnabled =
      !!rollingAverage?.enabled && !!rollingAverage?.windowSize;

    if (!hasViewerRA && !savedRAEnabled) return null;

    const savedTP = timePeriodSelection?.timePeriod;
    const timePeriod = (viewerTP ?? savedTP) as TimePeriod | undefined;
    if (!timePeriod) return null;

    const effectiveWindowSize = hasViewerRA
      ? (viewerRA as number)
      : (rollingAverage!.windowSize as number);

    if (viewerTP && viewerTP !== savedTP) {
      const resolved = getTimePeriodDates(
        viewerTP,
        dataAvailabilityDate ?? null,
      );
      return getRollingAverageComparisonFooterText(
        effectiveWindowSize,
        timePeriod,
        resolved?.startDate,
        resolved?.endDate,
      );
    }

    return getRollingAverageComparisonFooterText(
      effectiveWindowSize,
      timePeriod,
      timePeriodSelection?.startDate,
      timePeriodSelection?.endDate,
    );
  }, [
    viewerState?.rollingAverage,
    viewerState?.timePeriod,
    rollingAverage,
    timePeriodSelection?.timePeriod,
    timePeriodSelection?.startDate,
    timePeriodSelection?.endDate,
    dataAvailabilityDate,
  ]);

  // Calculate missing data warning when rolling window extends before dataset start
  const missingDataWarning = React.useMemo(() => {
    if (!rollingAverage?.enabled || !rollingAverage.windowSize) {
      return null;
    }

    const timePeriod = timePeriodSelection?.timePeriod;
    if (!timePeriod) return null;

    // Map period indices to actual period labels
    let mappedMissingPeriodLabels = missingRollingPeriodLabels;

    if (
      missingRollingPeriodLabels &&
      missingRollingPeriodLabels.length > 0 &&
      timePeriodSelection
    ) {
      // Helper function to calculate previous period label based on lookback
      const getPreviousPeriodLabel = (
        currentStartDate: string,
        currentEndDate: string,
        timePeriod: TimePeriod,
        lookback: number,
      ): string => {
        // For all period types, calculate using date formula
        // since we don't have enough info to determine the exact period type
        const start = new Date(currentStartDate);
        const end = new Date(currentEndDate);
        const periodLength = end.getTime() - start.getTime();

        const prevStart = new Date(start.getTime() - lookback * periodLength);
        const prevEnd = new Date(end.getTime() - lookback * periodLength);

        const formatDate = (date: Date) => {
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const year = date.getFullYear();
          return `${month}/${day}/${year}`;
        };

        return `${formatDate(prevStart)} - ${formatDate(prevEnd)}`;
      };

      // Map indices to actual period labels
      mappedMissingPeriodLabels = missingRollingPeriodLabels
        .map((indexOrLabel) => {
          // Check if it's a numeric index (1-based)
          const index = parseInt(indexOrLabel, 10);
          if (
            !isNaN(index) &&
            index > 0 &&
            timePeriodSelection.startDate &&
            timePeriodSelection.endDate
          ) {
            return getPreviousPeriodLabel(
              timePeriodSelection.startDate,
              timePeriodSelection.endDate,
              timePeriod,
              index,
            );
          }
          // If it's already a label, return as-is
          return indexOrLabel;
        })
        .filter(Boolean);
    }

    return getMissingDataWarning(
      rollingAverage.windowSize,
      effectiveRollingPeriodCount,
      mappedMissingPeriodLabels,
    );
  }, [
    rollingAverage,
    effectiveRollingPeriodCount,
    missingRollingPeriodLabels,
    timePeriodSelection,
  ]);

  // Filter and order data to exclude hidden rows and respect rowOrder
  const filteredData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    // First filter out hidden rows
    const visibleRows = data.filter((row) => {
      const rowId = String(row.id || row[rowLabelColumn]);
      return !hiddenRows.includes(rowId);
    });

    // If no rowOrder is provided, return filtered data as-is
    if (!rowOrder || rowOrder.length === 0) {
      return visibleRows;
    }

    // Create a map for quick lookup
    const rowsMap = new Map(
      visibleRows.map((row) => [String(row.id || row[rowLabelColumn]), row]),
    );

    // Order rows according to rowOrder, filtering out non-existent items
    const orderedRows = rowOrder
      .map((id) => rowsMap.get(id))
      .filter(
        (row): row is Record<string, string | number> => row !== undefined,
      );

    // Append any visible rows not present in rowOrder (e.g. new data from API)
    const orderedIds = new Set(rowOrder);
    const extraRows = visibleRows.filter(
      (row) => !orderedIds.has(String(row.id || row[rowLabelColumn])),
    );

    return [...orderedRows, ...extraRows];
  }, [data, hiddenRows, rowLabelColumn, rowOrder]);

  // Auto-detect columns from data (excluding id, rowLabelColumn, hidden columns, and internal keys)
  // and order them according to columnOrder
  const detectedColumns = React.useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    // Collect column fields from ALL rows to handle cases where
    // different rows have different numbers of descriptiveMetrics
    const columnFieldSet = new Set<string>();
    filteredData.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (
          key !== "id" &&
          key !== rowLabelColumn &&
          key !== "_metrics" &&
          key !== "enableStatTesting" &&
          key !== "enableRowStatTesting" &&
          !hiddenColumns.includes(key)
        ) {
          columnFieldSet.add(key);
        }
      });
    });
    const columnFields = Array.from(columnFieldSet);

    // If no columnOrder is provided, use the natural order
    const orderedFields =
      columnOrder && columnOrder.length > 0
        ? columnOrder.filter((id) => columnFields.includes(id))
        : columnFields;

    return orderedFields.map((field) => ({
      field,
      // Use custom label if available, otherwise use original field name
      headerName: customColumnLabels[field] || field,
    }));
  }, [
    filteredData,
    rowLabelColumn,
    hiddenColumns,
    customColumnLabels,
    columnOrder,
  ]);

  // Early returns after all hooks are called
  if (!data || data.length === 0) {
    return (
      <div className="w-full h-full bg-white flex flex-col items-center justify-center">
        <NoDataIcon
          style={{
            width: "110px",
            height: "110px",
            color: "var(--color-hp-bg-disabled)",
          }}
        />
        <p className="text-sm font-medium">
          {hasActiveFilters
            ? "No data for current filter combination."
            : "No data available."}
        </p>
      </div>
    );
  }

  const allRowsHidden =
    data &&
    data.length > 0 &&
    data.every((row) => {
      const rowId = String(row.id || row[rowLabelColumn]);
      return hiddenRows.includes(rowId);
    });

  if (allRowsHidden) {
    return (
      <div className="w-full h-full bg-white flex flex-col items-center justify-center">
        <NoDataIcon
          style={{
            width: "110px",
            height: "110px",
            color: "var(--color-hp-bg-disabled)",
          }}
        />
        <p className="text-sm font-medium">No data available.</p>
      </div>
    );
  }

  if (isStatTestingSuppressed && dashboardFilterNote) {
    return (
      <div className="w-full h-full bg-white flex flex-col">
        <EmptyChartState message={dashboardFilterNote} />
      </div>
    );
  }

  // Define the first column (row labels) with truncation and tooltip
  const labelColumn: GridColDef = {
    field: rowLabelColumn,
    headerName: "",
    flex: 1,
    minWidth: 250,
    headerAlign: "left",
    align: "left",
    sortable: false,
    disableColumnMenu: true,
    resizable: false,
    renderCell: (params) => {
      const rowId = String(params.row.id || params.value);
      // Use custom label if available, otherwise use original value
      const text = customRowLabels[rowId] || String(params.value || "");
      return (
        <HPTooltip title={text} placement="top" arrow>
          <Box
            sx={{
              fontWeight: 700,
              borderRight: "1px solid #E0E0E0",
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              paddingRight: "8px",
            }}
          >
            {text}
          </Box>
        </HPTooltip>
      );
    },
  };

  // Define value columns from detected columns
  const valueColumns: GridColDef[] = detectedColumns.map((col) => ({
    field: col.field,
    headerName: col.headerName,
    flex: 1,
    minWidth: 100,
    headerAlign: "center",
    align: "center",
    sortable: false,
    disableColumnMenu: true,
    resizable: false,
    renderCell: (params) => {
      const value = params.value;
      // Get metrics from the _metrics object
      const metrics = params.row._metrics?.[col.field];
      const statSignificance = metrics?.statSignificance;
      const rowStatSignificance = metrics?.rowStatSignificance;
      const refColumnStatSignificance = metrics?.refColumnStatSignificance;

      // Format the value
      let formattedValue: string;
      if (value === undefined || value === null) {
        formattedValue = "-";
      } else if (typeof value === "number") {
        formattedValue =
          Number(value.toFixed(decimals)).toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          }) + (showPercentSign ? "%" : "");
      } else {
        formattedValue = String(value);
      }

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            width: "100%",
          }}
        >
          <span>{formattedValue}</span>

          {statisticalTestingEnabled && (
            <>
              {/* Time period comparison arrows */}
              <div style={{ width: "16px", textAlign: "center" }}>
                {statSignificance === 1 && (
                  <span className="text-[#10B981]">▲</span>
                )}
                {statSignificance === -1 && (
                  <span className="text-[#EF4444]">▼</span>
                )}
              </div>
              {/* Row comparison to total signs (Overall Total mode only) */}
              {isOverallTotalMode && (
                <div style={{ width: "16px", textAlign: "center" }}>
                  {rowStatSignificance === 1 && (
                    <span className="text-[#10B981]">+</span>
                  )}
                  {rowStatSignificance === -1 && (
                    <span className="text-[#EF4444]">−</span>
                  )}
                </div>
              )}
              {/* Reference column comparison signs (Reference Column mode only) */}
              {isReferenceColumnMode && (
                <div style={{ width: "16px", textAlign: "center" }}>
                  {refColumnStatSignificance === 1 && (
                    <span className="text-[#10B981]">+</span>
                  )}
                  {refColumnStatSignificance === -1 && (
                    <span className="text-[#EF4444]">−</span>
                  )}
                </div>
              )}
            </>
          )}
        </Box>
      );
    },
  }));

  const allColumns: GridColDef[] = [labelColumn, ...valueColumns];

  // Transform filtered data to match DataGrid requirements
  let rows: GridRowsProp = filteredData.map((item, index) => ({
    id: item.id || index + 1,
    ...item,
  }));

  // Add N values row ONLY if showN is explicitly true AND we have valid column N values
  if (
    showN === true &&
    columnNValues &&
    Object.keys(columnNValues).length > 0
  ) {
    const nRow: Record<string, string | number> = {
      id: "n-values-row",
      [rowLabelColumn]: "N",
    };

    // Add N values for each column, use "-" if undefined
    detectedColumns.forEach((col) => {
      const nValue = columnNValues[col.field];
      nRow[col.field] = nValue !== undefined ? nValue : "-";
    });

    rows = [...rows, nRow];
  }

  return (
    <Paper
      elevation={0}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      {title && <Typography variant="h6">{title}</Typography>}
      <Box
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
        }}
      >
        <DataGrid
          rows={rows}
          columns={allColumns}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnResize
          hideFooter
          columnHeaderHeight={56}
          rowHeight={52}
          getRowClassName={(params) => {
            // Add special styling for N row
            if (params.row.id === "n-values-row") {
              return "n-values-row";
            }
            return "";
          }}
          sx={{
            height: "100%",
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-columnHeader": {
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            },
            // Style for N row - darker background
            "& .n-values-row": {
              backgroundColor: "#f5f5f5",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#eeeeee",
              },
            },
          }}
        />
      </Box>
      {/* Footer Summary */}
      <Box
        sx={{
          mt: 1,
          p: 1,
          px: 2.5,
          backgroundColor: "#f9fafb",
          borderRadius: 1,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {dashboardFilterNote ? (
            <Typography variant="caption" color="primary" fontWeight={500}>
              {dashboardFilterNote}
            </Typography>
          ) : (
            !rollingAverageFooterText &&
            timePeriodFooterText && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                {timePeriodFooterText}
              </Typography>
            )
          )}
          {rollingAverageFooterText && (
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
            >
              {rollingAverageFooterText}
            </Typography>
          )}
          {/* {rollingAverageComparisonFooterText && (
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
            >
              {rollingAverageComparisonFooterText}
            </Typography>
          )}
          {rollingAverage?.enabled && comparisonRollingLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
            >
              {comparisonRollingLabel}
            </Typography>
          )} */}
          {rollingPeriodsWarningMessage && (
            <Typography variant="caption" color="warning.main" fontWeight={500}>
              {rollingPeriodsWarningMessage}
            </Typography>
          )}
          {missingDataWarning && (
            <Typography variant="caption" color="warning.main" fontWeight={500}>
              {missingDataWarning}
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          Weight:{" "}
          {weightOption === "none"
            ? "No Weight"
            : weightOption === "custom" && customWeight
              ? `Custom: ${customWeight.questionLabel} - ${customWeight.variableLabel}`
              : weightOption === "per-segment"
                ? "Per-Segment Weighting"
                : "Default Weight"}
        </Typography>
      </Box>
      {hasVisibleSuppressedCells && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            backgroundColor: "#f5f5f5",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Value suppressed due to low base size
          </Typography>
        </Box>
      )}
      {statisticalTestingEnabled &&
        !isStatTestingSuppressed &&
        (() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const hasAnySignificance = (metric: any) =>
            (metric?.statSignificance !== 0 &&
              metric?.statSignificance !== undefined) ||
            (metric?.rowStatSignificance !== 0 &&
              metric?.rowStatSignificance !== undefined) ||
            (metric?.refColumnStatSignificance !== 0 &&
              metric?.refColumnStatSignificance !== undefined);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const getMetrics = (row: any, hidden: boolean) =>
            row._metrics
              ? Object.entries(row._metrics)
                  .filter(([key]) =>
                    hidden
                      ? hiddenColumns.includes(key)
                      : !hiddenColumns.includes(key),
                  )
                  .map(([, val]) => val)
              : [];

          const hasTimePeriodSignificance = rows.some((row) =>
            getMetrics(row, false).some(
              (metric: any) =>
                metric?.statSignificance === 1 ||
                metric?.statSignificance === -1,
            ),
          );
          const hasRowSignificance =
            isOverallTotalMode &&
            rows.some((row) =>
              getMetrics(row, false).some(
                (metric: any) =>
                  metric?.rowStatSignificance === 1 ||
                  metric?.rowStatSignificance === -1,
              ),
            );
          const hasRefColumnSignificance =
            isReferenceColumnMode &&
            rows.some((row) =>
              getMetrics(row, false).some(
                (metric: any) =>
                  metric?.refColumnStatSignificance === 1 ||
                  metric?.refColumnStatSignificance === -1,
              ),
            );
          const significanceLevel =
            statisticalTesting.significanceLevel || "90%";
          const compareToTotal = statisticalTesting.compareToTotal || false;
          const isRollingAvg =
            rollingAverage?.enabled &&
            rollingAverage.windowSize !== undefined &&
            rollingAverage.windowSize > 0;
          // Use effectiveRollingPeriodCount (N = actual periods with data) if available,
          // otherwise fall back to configured windowSize
          const raWindowSize =
            effectiveRollingPeriodCount ?? rollingAverage?.windowSize;
          const periodType = timePeriodSelection?.timePeriod
            ? getPeriodTypeLabel(timePeriodSelection.timePeriod)
            : "period";
          const comparisonLabel = isRollingAvg
            ? `Previous Rolling Window`
            : `Previous Period`;
          const comparisonDescription = isRollingAvg
            ? `previous ${raWindowSize}-${periodType} rolling average`
            : `previous period`;

          // Build footer lines based on what's actually visible
          const footerLines: React.ReactNode[] = [];

          if (hasTimePeriodSignificance) {
            footerLines.push(
              <Typography
                key="time-period"
                variant="caption"
                color="text.secondary"
              >
                <span className="text-[#10B981]">▲</span> /{" "}
                <span className="text-[#EF4444]">▼</span> {comparisonLabel}:
                Significant increase/decrease compared to{" "}
                {comparisonDescription} at {significanceLevel} level of
                significance.
              </Typography>,
            );
          }

          if (compareToTotal && hasRowSignificance && isOverallTotalMode) {
            footerLines.push(
              <Typography
                key="total-row"
                variant="caption"
                color="text.secondary"
              >
                <span className="text-[#10B981]">+</span> /{" "}
                <span className="text-[#EF4444]">−</span> Total Row:
                Significantly higher/lower than total row at {significanceLevel}{" "}
                level of significance.
              </Typography>,
            );
          }

          if (
            compareToTotal &&
            hasRefColumnSignificance &&
            isReferenceColumnMode
          ) {
            const refLabel =
              customColumnLabels[referenceColumnEntity!] ||
              referenceColumnEntity;
            footerLines.push(
              <Typography
                key="ref-column"
                variant="caption"
                color="text.secondary"
              >
                <span className="text-[#10B981]">+</span> /{" "}
                <span className="text-[#EF4444]">−</span> Reference Column:
                Significantly higher/lower than at {significanceLevel} level of
                significance.
              </Typography>,
            );
          }

          // Only show footer if there are actual markers visible in the table
          if (footerLines.length > 0) {
            return (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  px: 2.5,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {footerLines}
              </Box>
            );
          }

          // No markers visible — don't show any footer
          return null;
        })()}
    </Paper>
  );
};

export default CrosstabTable;
